import { Text, Button } from "rizzui";
import {
  IEnquiryEntry,
  LeadFeedbackStatus,
} from "@/utilities/types/EnquiryEntry";
import formatPhone from "@/utilities/functions/format-phone";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import { getLeadSourceDisplay } from "@/utilities/functions/getLeadSourceDisplay";
import { BiSolidDislike, BiSolidLike } from "react-icons/bi";

// Constants for better maintainability
const FEEDBACK_BUTTON_HEIGHT = 54;
const ICON_SIZE = 20;

interface CardLeadFeedbackProps {
  lead: IEnquiryEntry;
  onFeedback: (id: string, type: LeadFeedbackStatus) => void;
  isSubmitting: boolean;
}

export default function CardLeadFeedback({
  lead,
  onFeedback,
  isSubmitting,
}: CardLeadFeedbackProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm w-full">
      <div className="flex flex-col gap-2">
        <div className="flex w-full items-center justify-between">
          <div className="text-gray-500 text-xs flex flex-row items-center justify-center">
            <span className="min-w-fit font-semibold">
              {convertToSingaporeTime(new Date(lead.createdAt))}
            </span>
            <span className="mx-1">•</span>
            <span className="min-w-fit">{getLeadSourceDisplay(lead)}</span>
          </div>
        </div>
        {/* Lead Name and Contact */}
        <div className="flex flex-row items-center justify-between gap-2 mb-2">
          <div className="w-max-[60%] min-w-[180px] break-words">
            <Text className="text-2xl font-bold text-gray-900">
              {lead.name || "N/A"}
            </Text>
            {lead.email && <Text className="text-gray-500">{lead.email}</Text>}
            {lead.phone && (
              <Text className="text-gray-500">{formatPhone(lead.phone)}</Text>
            )}
          </div>
          {/* Feedback buttons */}
          <div
            className="flex flex-shrink-0 flex-row items-center justify-center rounded-full overflow-hidden min-w-fit border border-gray-200"
            style={{ height: `${FEEDBACK_BUTTON_HEIGHT}px` }}
          >
            <Button
              variant="text"
              size="lg"
              className="flex flex-col items-center justify-center h-full !text-green-600"
              onClick={() => {
                if (lead.feedbackStatus === "good" || isSubmitting) return;
                onFeedback(lead.id, "good");
              }}
            >
              <BiSolidLike size={ICON_SIZE} />
              <Text className="text-xs font-semibold">Success</Text>
            </Button>
            <Button
              size="lg"
              variant="text"
              className="flex flex-col items-center justify-center h-full pl-0 !text-red-600"
              onClick={() => {
                if (lead.feedbackStatus === "bad" || isSubmitting) return;
                onFeedback(lead.id, "bad");
              }}
            >
              <BiSolidDislike size={ICON_SIZE} />
              <Text className="text-xs font-semibold">Failed</Text>
            </Button>
          </div>
        </div>
        {/* Project Info */}
        {lead.project && (
          <div className="w-full mb-2">
            <Text className="bg-blue-50 text-blue-600 rounded-full p-2 font-semibold max-w-fit text-ellipsis overflow-hidden text-nowrap">
              {typeof lead.project.name === "object"
                ? lead.project.name.en
                : lead.project.name}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
